/**
 * User Dashboard API Service
 * Handles all API calls for user dashboard functionality
 */

import { request } from './apiClient';

/**
 * Dashboard Service
 */
const dashboardService = {
    /**
     * Get dashboard overview
     * @returns {Promise<Object>} Dashboard data with statistics and activity
     */
    getDashboardOverview: async () => {
        return request('/dashboard');
    },

    /**
     * Get the count of unread notifications, for badges/polling
     * @returns {Promise<number>}
     */
    getUnreadNotificationCount: async () => {
        const data = await request('/dashboard/notifications?read=false&limit=1');
        return data.data?.pagination?.totalNotifications || 0;
    },

    /**
     * Get saved properties
     * @returns {Promise<Array>} List of saved properties
     */
    getSavedProperties: async () => {
        const data = await request('/dashboard/saved-properties');
        return data.data?.properties || [];
    },

    /**
     * Save a property
     * @param {Object} propertyData - Property save data
     * @param {number} propertyData.propertyId - Property ID
     * @param {string} [propertyData.collectionName] - Collection name
     * @param {string} [propertyData.notes] - Personal notes
     * @param {number} [propertyData.priority] - Priority 0-10
     * @returns {Promise<Object>}
     */
    saveProperty: async (propertyData) => {
        return request('/dashboard/save-property', {
            method: 'POST',
            body: {
                propertyId: propertyData.propertyId,
                collectionName: propertyData.collectionName || 'My Favorites',
                notes: propertyData.notes || '',
                priority: propertyData.priority || 0
            }
        });
    },

    /**
     * Remove saved property
     * @param {number} propertyId - Property ID to remove
     * @returns {Promise<Object>}
     */
    unsaveProperty: async (propertyId) => {
        return request(`/dashboard/unsave-property?propertyId=${propertyId}`, {
            method: 'DELETE'
        });
    },

    /**
     * Get application history
     * @param {string} [status] - Filter by status
     * @returns {Promise<Array>} List of applications
     */
    getApplications: async (status = null) => {
        const path = status
            ? `/dashboard/applications?status=${status}`
            : '/dashboard/applications';
        const data = await request(path);
        return data.data?.applications || [];
    },

    /**
     * Get messages
     * @param {string} [type='received'] - Message type: 'received' or 'sent'
     * @returns {Promise<Array>} List of messages
     */
    getMessages: async (type = 'received') => {
        const data = await request(`/dashboard/messages?type=${type}`);
        return data.data?.messages || [];
    },

    /**
     * Look up a user by email or phone, to resolve a recipient for
     * messaging without already knowing their numeric user ID.
     * NOTE: this endpoint doesn't exist on the backend yet.
     * @param {Object} query
     * @param {string} [query.email]
     * @param {string} [query.phone]
     * @returns {Promise<{userId: number, name: string, email: string, phone: string}|null>}
     */
    findRecipient: async ({ email, phone }) => {
        const param = email ? `email=${encodeURIComponent(email)}` : `phone=${encodeURIComponent(phone)}`;
        const data = await request(`/dashboard/users/lookup?${param}`);
        return data.data || null;
    },

    /**
     * Send a message
     * @param {Object} messageData - Message data
     * @param {number} messageData.recipientId - Recipient user ID
     * @param {string} messageData.message - Message content
     * @returns {Promise<Object>}
     */
    sendMessage: async (messageData) => {
        if (!messageData.message || !messageData.message.trim()) {
            throw new Error('Message cannot be empty');
        }

        return request('/dashboard/send-message', {
            method: 'POST',
            body: {
                recipientId: messageData.recipientId,
                message: messageData.message.trim()
            }
        });
    },

    /**
     * Get user settings
     * @returns {Promise<Object>} User settings and preferences
     */
    getSettings: async () => {
        const data = await request('/dashboard/settings');

        // Parse JSON string fields
        if (data.data) {
            const settings = data.data;

            return {
                ...settings,
                searchPreferences: {
                    ...settings.searchPreferences,
                    preferredLocations: settings.searchPreferences.preferredLocations
                        ? JSON.parse(settings.searchPreferences.preferredLocations)
                        : [],
                    preferredPropertyTypes: settings.searchPreferences.preferredPropertyTypes
                        ? JSON.parse(settings.searchPreferences.preferredPropertyTypes)
                        : [],
                    preferredAmenities: settings.searchPreferences.preferredAmenities
                        ? JSON.parse(settings.searchPreferences.preferredAmenities)
                        : [],
                    budgetRange: settings.searchPreferences.budgetRange
                        ? JSON.parse(settings.searchPreferences.budgetRange)
                        : { min: 0, max: 100000 },
                    roomPreferences: settings.searchPreferences.roomPreferences
                        ? JSON.parse(settings.searchPreferences.roomPreferences)
                        : { bedrooms: [], bathrooms: [] }
                },
                notificationPreferences: settings.notificationPreferences
                    ? JSON.parse(settings.notificationPreferences)
                    : {},
                viewingPreferences: settings.viewingPreferences
                    ? JSON.parse(settings.viewingPreferences)
                    : {}
            };
        }

        return data.data;
    },

    /**
     * Update user settings
     * @param {Object} updates - Settings to update (partial update)
     * @returns {Promise<Object>}
     */
    updateSettings: async (updates) => {
        return request('/dashboard/settings/update', {
            method: 'POST',
            body: updates
        });
    }
};

export default dashboardService;
